<template>
  <div
    class="lease-placeholder"
    :class="$theme.darkTheme ? 'lease-placeholder--dark' : 'lease-placeholder--light'"
  >
    <div class="lease-placeholder__inner">
      <i class="material-icons lease-placeholder__icon" :style="{ color: accent }">{{ iconName }}</i>
      <h5 class="lease-placeholder__title">{{ title }}</h5>
      <p class="lease-placeholder__body">{{ body }}</p>
      <button
        v-if="showReconnect && isConnected"
        class="lease-placeholder__btn"
        :style="{ background: accent, color: accentText }"
        :disabled="isLoading"
        @click="handleReconnect"
      >{{ isLoading ? 'Reconectando...' : 'Reconectar agora' }}</button>
      <p v-else-if="showReconnect" class="lease-placeholder__offline">
        <i class="material-icons">wifi_off</i>
        <span>Você está offline</span>
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

// Poster que substitui o player quando o lease do enroll bloqueia a aula
// baixada (expired/revoked/unknown). Sem isto, o player renderiza com src nulo
// (a chave AES é negada nativamente) e vira um elemento "fantasma" que parece
// tocável mas nunca carrega. Espelha as mensagens/ação do LeaseBlockedModal.
export default {
  name: 'LeaseBlockedPlaceholder',
  props: {
    // Estado do lease: 'expired' | 'revoked' | 'unknown'.
    mode: { type: String, default: 'expired' },
    enrollId: { type: [String, Number], default: null }
  },
  data () {
    return { isLoading: false };
  },
  computed: {
    ...mapGetters({ isConnected: 'dom/isConnected' }),
    iconName () { return this.mode === 'revoked' ? 'block' : 'cloud_off'; },
    title () {
      if (this.mode === 'revoked') return 'Acesso suspenso';
      return 'Reconecte para continuar';
    },
    body () {
      if (this.mode === 'revoked') {
        return 'Sua assinatura foi suspensa. Regularize para voltar a acessar este conteúdo.';
      }
      return 'Seu acesso offline expirou. Conecte-se à internet para revalidar sua matrícula e continuar assistindo.';
    },
    // No modo 'revoked' o caminho é regularizar a assinatura (outra tela), não
    // reconectar — esconde o botão pra não confundir.
    showReconnect () { return this.mode !== 'revoked'; },
    accent () { return this.$theme.primaryColor || '#0564b1'; },
    accentText () { return this.$theme.primaryText || '#fff'; }
  },
  methods: {
    async handleReconnect () {
      if (!this.enrollId) return;
      this.isLoading = true;
      try {
        await this.$store.dispatch('lease/forceRevalidate', { enrollId: this.enrollId });
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.lease-placeholder {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  // Fallback pra WebView sem suporte a aspect-ratio (iOS <15 / WebView antigo):
  // garante que o poster não colapse pra uma faixa fina.
  min-height: 180px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &--dark { background: #16161f; }
  &--light { background: #0f0f14; } // escuro em ambos: ocupa o lugar do vídeo
}

.lease-placeholder__inner {
  text-align: center;
  padding: 20px 24px;
  max-width: 420px;
}

.lease-placeholder__icon { font-size: 40px; }

.lease-placeholder__title {
  color: #f0f0f0;
  font-size: 16px;
  font-weight: 600;
  margin: 10px 0 8px;
}

.lease-placeholder__body {
  color: #a9a9bd;
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 18px;
}

.lease-placeholder__btn {
  border: none;
  border-radius: 8px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

// Estado (não ação): sinaliza offline sem parecer um botão clicável.
.lease-placeholder__offline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #8b8b9e;
  font-size: 12px;
  font-weight: 500;

  i { font-size: 16px; }
}
</style>
